/**
 * Type definitions for Spotlight Landing Page structures
 * Replaces loose 'any' types with proper TypeScript interfaces
 */

export interface PractitionerInfo {
  name: string
  specialty: string
  photo_url?: string
  logo_url?: string
}

export type CTAType = 'book_call' | 'ask_question'
export type CTAMode = 'calendar_only' | 'lead_form_then_schedule'
export type BookCallDestination = 'calendar' | 'email'
export type FormField = 'name' | 'email' | 'phone' | 'message'

export interface CTAConfig {
  cta_type: CTAType
  cta_mode?: CTAMode
  book_call_destination?: BookCallDestination
  booking_url?: string
  contact_email?: string
  form_fields?: FormField[]
  required_fields?: FormField[]
}

export interface HeroSection {
  headline: string
  subheadline: string
}

export interface OfferSection {
  headline: string
  bullets: string[]
}

export interface TrustSection {
  headline: string
  bullets: string[] // Testimonials
}

export interface AboutSection {
  headline: string
  body: string
}

export interface FAQItem {
  q: string
  a: string
}

export interface FAQSection {
  headline: string
  items: FAQItem[]
}

export interface CTASection {
  headline: string
  button_text: string
  microcopy?: string
}

export interface LandingPageSections {
  hero: HeroSection
  offer: OfferSection
  trust: TrustSection
  about: AboutSection
  faq: FAQSection
  cta: CTASection
}

export interface SocialLink {
  platform: string
  url: string
}

export interface LinksConfig {
  website_url?: string
  social_links?: SocialLink[]
}

export interface LandingPageDraft {
  practitioner: PractitionerInfo
  cta_config: CTAConfig
  sections: LandingPageSections
  links?: LinksConfig
}

// Follow-up automation types
export type EmailChannel = 'email'

export interface FollowUpStep {
  delay_minutes: number
  channel: EmailChannel
  subject: string
  body: string
}

export interface FollowUpSequence {
  enabled: boolean
  template_id?: string
  steps: FollowUpStep[]
}

export interface EmailAutoresponder {
  enabled: boolean
  from_name?: string
  from_email?: string
  subject?: string
  body?: string
}

export interface ConversionReadinessCheck {
  ok: boolean
  message?: string
}

export interface ConversionReadinessStatus {
  is_ready: boolean
  checks: Record<string, ConversionReadinessCheck>
}

export interface ConversionReadiness {
  status?: ConversionReadinessStatus
  notification_emails?: string[]
  email_autoresponder?: EmailAutoresponder
  followup_sequence?: FollowUpSequence
}

// Validation helpers
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
  } catch {
    return false
  }
}

// Type guards for runtime validation
export const isLandingPageComplete = (draft: Partial<LandingPageDraft>): draft is LandingPageDraft => {
  if (!draft.sections) return false

  const { hero, offer, trust, about, faq, cta } = draft.sections

  return !!(
    hero?.headline?.trim() &&
    hero?.subheadline?.trim() &&
    offer?.bullets?.filter(b => b?.trim()).length >= 3 &&
    trust?.bullets?.filter(b => b?.trim()).length >= 2 &&
    about?.body?.trim() &&
    faq?.items?.filter(i => i?.q && i?.a).length >= 2 &&
    cta?.headline?.trim()
  )
}
